import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { fullBlogPosts } from '../data/mock';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Clock, Calendar, User, Tag, Share2, Linkedin, Twitter, Facebook, ArrowRight } from 'lucide-react';
import { SEO, ArticleSchema } from '../components/SEO';

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = fullBlogPosts.find(p => p.slug === slug);
  
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = fullBlogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  const shareUrl = window.location.href;
  const shareText = encodeURIComponent(post.title);

  return (
    <div className="min-h-screen">
      <SEO
        title={post.title}
        path={`/blog/${post.slug}`}
        description={post.excerpt}
        image={post.image}
        type="article"
        article={{ date: post.date, author: post.author, tags: post.tags }}
      />
      <ArticleSchema
        title={post.title}
        description={post.excerpt}
        url={`/blog/${post.slug}`}
        image={post.image}
        datePublished={post.date}
        author={post.author}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to="/blog" 
              className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-3 mb-6">
              <Badge variant="secondary" className="bg-teal-500/20 text-teal-400">
                {post.category}
              </Badge>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>
            
            <p className="text-xl text-slate-300 mb-8">
              {post.excerpt}
            </p>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {post.author}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(post.date).toLocaleDateString('en-AU', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {post.readTime}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-3">
                <article className="prose prose-lg max-w-none prose-headings:text-navy-900 prose-headings:font-bold prose-p:text-slate-600 prose-a:text-teal-600 prose-strong:text-navy-900 prose-li:text-slate-600">
                  {post.content.split('\n').map((paragraph, index) => {
                    if (paragraph.startsWith('## ')) {
                      return (
                        <h2 key={index} className="text-2xl font-bold text-navy-900 mt-10 mb-4">
                          {paragraph.replace('## ', '')}
                        </h2>
                      );
                    } else if (paragraph.startsWith('### ')) {
                      return (
                        <h3 key={index} className="text-xl font-semibold text-navy-900 mt-8 mb-3">
                          {paragraph.replace('### ', '')}
                        </h3>
                      );
                    } else if (paragraph.startsWith('#### ')) {
                      return (
                        <h4 key={index} className="text-lg font-semibold text-navy-900 mt-6 mb-2">
                          {paragraph.replace('#### ', '')}
                        </h4>
                      );
                    } else if (paragraph.startsWith('- ')) {
                      return (
                        <li key={index} className="text-slate-600 ml-4">
                          {paragraph.replace('- ', '')}
                        </li>
                      );
                    } else if (paragraph.match(/^\d+\. /)) {
                      return (
                        <li key={index} className="text-slate-600 ml-4 list-decimal">
                          {paragraph.replace(/^\d+\. /, '')}
                        </li>
                      );
                    } else if (paragraph.trim() !== '') {
                      return (
                        <p key={index} className="text-slate-600 mb-4 leading-relaxed">
                          {paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}
                        </p>
                      );
                    }
                    return null;
                  })}
                </article>

                {/* Tags */}
                <div className="mt-12 pt-8 border-t border-slate-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag className="h-4 w-4 text-slate-500" />
                    {post.tags.map((tag) => (
                      <span 
                        key={tag}
                        className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Share */}
                <div className="mt-8 pt-8 border-t border-slate-200">
                  <div className="flex items-center gap-4">
                    <span className="text-slate-600 font-medium flex items-center gap-2">
                      <Share2 className="h-4 w-4" />
                      Share this article:
                    </span>
                    <a 
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <Linkedin className="h-5 w-5 text-slate-600" />
                    </a>
                    <a 
                      href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <Twitter className="h-5 w-5 text-slate-600" />
                    </a>
                    <a 
                      href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <Facebook className="h-5 w-5 text-slate-600" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-32 space-y-8">
                  {/* CTA Card */}
                  <div className="p-6 bg-navy-900 rounded-2xl text-white">
                    <h3 className="font-semibold mb-3">Need Trade Support?</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      Our team can help you navigate international trade requirements.
                    </p>
                    <Link 
                      to="/contact"
                      className="inline-flex items-center text-teal-400 hover:text-teal-300 text-sm font-medium"
                    >
                      Contact Us
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>

                  {/* Newsletter */}
                  <div className="p-6 bg-slate-50 rounded-2xl">
                    <h3 className="font-semibold text-navy-900 mb-3">Stay Updated</h3>
                    <p className="text-slate-600 text-sm mb-4">
                      Get the latest trade insights delivered to your inbox.
                    </p>
                    <input
                      type="email"
                      placeholder="Your email"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <button className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <section className="py-16 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-8">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                    <div className="group flex gap-6 p-6 bg-white rounded-2xl border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300">
                      <div className="w-32 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                        <img 
                          src={relatedPost.image} 
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1">
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700 text-xs mb-2">
                          {relatedPost.category}
                        </Badge>
                        <h3 className="font-semibold text-navy-900 group-hover:text-teal-700 transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        <p className="text-sm text-slate-500 mt-2">{relatedPost.readTime}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-navy-900 mb-4">
              Ready to Start Trading Globally?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Partner with Naviton Global for reliable import and export solutions.
            </p>
            <Link to="/contact">
              <button className="inline-flex items-center px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors">
                Get in Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
